import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import InventoryServices from "../../services/InventoryServices";
import translations from "../../assets/locals/translations";

const EditDeliveryData = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const currentLanguage = useSelector((state) => state.language.language);
    const t = translations[currentLanguage] || translations["en"];

    const InventoryService = new InventoryServices();

    const { data: editData } = location?.state ?? {};

    const [modelslist, setModelslist] = useState([]);
    const [districtList, setDistrictList] = useState([]);
    const [talukaList, setTalukaList] = useState([]);
    const [filterdTalukaList, setfilterdTalukaList] = useState([]);
    const [villageList, setVillageList] = useState([]);
    const [filteredVillageList, setFileredVillageList] = useState([]);
    const [inventoryItem, setInventoryItem] = useState({ rawData: [], chassisList: [], engineList: [] });
    const [loading, setLoading] = useState(true);

    const DeliveryFormSchema = useMemo(() => {
        return Yup.object().shape({
            customer_name: Yup.string().required(t.required || "Required"),
            model_id: Yup.string().required(t.required || "Required"),
            chassis: Yup.string().required(t.required || "Required"),
            engine: Yup.string().required(t.required || "Required"),
            district: Yup.string().required(t.required || "Required"),
            taluka: Yup.string().required(t.required || "Required"),
            village: Yup.string().required(t.required || "Required"),
        });
    }, [t]);

    const [initialValues, setInitialValues] = useState({
        bil_no: "", date: "", ndp: "", number22: "", bum_cod: "", model_id: "", chassis: "",
        engine: "", delivery_date: "", phone_number: "", customer_name: "",
        district: "", taluka: "", village: "",
    });

    const getModelStockData = async (modelId, currentChassis, currentEngine) => {
        try {
            const response = await InventoryService.getModelStockData(modelId);
            if (response && response.isSuccess) {
                const rawData = response.result;
                let chassisOptions = rawData.map((item) => ({ label: item.chassis, value: item.chassis }));
                let engineOptions = rawData.map((item) => ({ label: item.engine, value: item.engine }));

                if (currentChassis && !chassisOptions.find(i => i.value === currentChassis))
                    chassisOptions.push({ label: currentChassis, value: currentChassis });
                if (currentEngine && !engineOptions.find(i => i.value === currentEngine))
                    engineOptions.push({ label: currentEngine, value: currentEngine });

                setInventoryItem({ rawData, chassisList: chassisOptions, engineList: engineOptions });
            }
        } catch (e) { console.error(e); }
    };

    const getMasterData = async () => {
        const url = `${process.env.REACT_APP_NODE_URL}/api/master/get-make-modal-list`;
        const config = { headers: { token: localStorage.getItem("rbacToken") } };
        try {
            const response = await axios.get(url, config);
            if (response?.data?.isSuccess) {
                const res = response.data.result;
                setModelslist(res.modelslistByAgencyMake || []);
                setDistrictList(res.districtList || []);
                setTalukaList(res.talukaList || []);
                setVillageList(res.villageList || []);
                return res;
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        const mountData = async () => {
            const masters = await getMasterData();
            if (editData && masters) {
                // Find IDs based on Names from table
                const foundModel = masters.modelslistByAgencyMake.find(m => m.modalName === editData.model_name || m.label === editData.model_name);
                const foundDist = masters.districtList.find(d => d.label === editData.district);
                const foundTaluka = masters.talukaList.find(t => t.label === editData.taluka);
                const foundVillage = masters.villageList.find(v => v.label === editData.village);

                const districtId = foundDist?.value || editData.district_id || "";
                const talukaId = foundTaluka?.value || editData.taluka_id || "";
                const modelId = foundModel?.id || foundModel?.value || editData.modal_id || "";

                setInitialValues({
                    bil_no: editData.bil_no || "",
                    date: editData.date ? moment(editData.date).format("YYYY-MM-DD") : "",
                    ndp: editData.ndp || "",
                    number22: editData.number22 || "",
                    bum_cod: editData.bum_cod || "",
                    model_id: modelId,
                    chassis: editData.chassis || "",
                    engine: editData.engine || "",
                    delivery_date: editData.delivery_date ? moment(editData.delivery_date).format("YYYY-MM-DD") : "",
                    phone_number: editData.phone_number || "",
                    customer_name: editData.customer_name || "",
                    district: districtId,
                    taluka: talukaId,
                    village: foundVillage?.value || editData.village_id || "",
                });

                // Pre-filter dropdowns so values appear
                if (districtId) setfilterdTalukaList(masters.talukaList.filter(t => t.district_id == districtId));
                if (talukaId) setFileredVillageList(masters.villageList.filter(v => v.taluka_id == talukaId));
                if (modelId) await getModelStockData(modelId, editData.chassis, editData.engine);

                setLoading(false);
            } else if (!editData) {
                navigate(-1);
            }
        };
        mountData();
    }, [editData]);

    const handleSubmit = async (values) => {
        try {
            const deliveryId = editData?.deliveryId || editData?.id || editData?.delivery_id;
            if (!deliveryId) {
                dispatch(setShowMessage("Unable to identify delivery ID for update"));
                return;
            }

            const payload = {
                ...values,
                deliveryId,
                editType: "DELIVERY",
            };
            console.log("Edit payload", payload);
            const response = await InventoryService.editDeliveryData(payload);
            console.log("Edit Response:", response);

            if (response?.success) {
                dispatch(setShowMessage(response.message || "Updated successfully"));
                navigate(-1);
                return;
            }

            dispatch(setShowMessage(response?.message || "Update failed. Please try again."));
        } catch (e) {
            console.error(e);
            dispatch(setShowMessage("Server error while updating delivery."));
        }
    };

    if (loading) return <div className="p-5 text-center">Loading and Mounting Data...</div>;

    return (
        <Container className="mt-4">
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={DeliveryFormSchema}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue }) => (
                    <Form>

                        <Card className="shadow-sm border-0 mx-auto" style={{ maxWidth: 1100 }}>
                            <Card.Header className="bg-white border-0 pt-4 px-4 pb-3">
                                <div className="d-flex align-items-start justify-content-between gap-3">
                                    <div>
                                        <div className="text-muted small mb-1">Delivery</div>
                                        <h4 className="text-primary fw-bold m-0">Edit Delivery</h4>
                                        <div className="text-muted small mt-1">
                                            Update customer and delivery details.
                                        </div>
                                    </div>
                                    <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                                        Back
                                    </Button>
                                </div>
                            </Card.Header>

                            <Card.Body className="px-4 pb-4 pt-2">
                                {/* CUSTOMER DETAILS */}
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="fw-semibold text-dark">Customer Details</div>
                                </div>
                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <label className="form-label fw-semibold">
                                            Customer Name <span className="text-danger">*</span>
                                        </label>
                                        <Field name="customer_name" className="form-control" />
                                        <ErrorMessage name="customer_name" component="div" className="text-danger small" />
                                    </Col>

                                    <Col md={6}>
                                        <label className="form-label fw-semibold">Phone Number</label>
                                        <Field name="phone_number" className="form-control" />
                                    </Col>
                                </Row>

                                <hr className="my-2" />

                                {/* LOCATION */}
                                <div className="fw-semibold text-dark mt-3 mb-2">Location Details</div>
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <label className="form-label fw-semibold">
                                            District <span className="text-danger">*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="district"
                                            className="form-select"
                                            onChange={(e) => {
                                                setFieldValue("district", e.target.value);
                                                setfilterdTalukaList(
                                                    talukaList.filter(t => t.district_id == e.target.value)
                                                );
                                                setFieldValue("taluka", "");
                                                setFieldValue("village", "");
                                            }}
                                        >
                                            <option value="">Select District</option>
                                            {districtList.map(d => (
                                                <option key={d.value} value={d.value}>{d.label}</option>
                                            ))}
                                        </Field>
                                    </Col>

                                    <Col md={4}>
                                        <label className="form-label fw-semibold">
                                            Taluka <span className="text-danger">*</span>
                                        </label>
                                        <Field
                                            as="select"
                                            name="taluka"
                                            className="form-select"
                                            onChange={(e) => {
                                                setFieldValue("taluka", e.target.value);
                                                setFileredVillageList(
                                                    villageList.filter(v => v.taluka_id == e.target.value)
                                                );
                                                setFieldValue("village", "");
                                            }}
                                        >
                                            <option value="">Select Taluka</option>
                                            {filterdTalukaList.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </Field>
                                    </Col>

                                    <Col md={4}>
                                        <label className="form-label fw-semibold">
                                            Village <span className="text-danger">*</span>
                                        </label>
                                        <Field as="select" name="village" className="form-select">
                                            <option value="">Select Village</option>
                                            {filteredVillageList.map(v => (
                                                <option key={v.value} value={v.value}>{v.label}</option>
                                            ))}
                                        </Field>
                                    </Col>
                                </Row>

                                <hr className="my-2" />

                                {/* DELIVERY DETAILS */}
                                <div className="fw-semibold text-dark mt-3 mb-2">Delivery Details</div>
                                <Row className="g-3 mb-4">
                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">Bill No</label>
                                        <Field name="bil_no" className="form-control bg-light" readOnly disabled />
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">Bill Date</label>
                                        <Field name="date" type="date" className="form-control" />
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">Delivery Date</label>
                                        <Field name="delivery_date" type="date" className="form-control" />
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">BUM COD</label>
                                        <Field name="bum_cod" className="form-control" />
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">NDP</label>
                                        <Field name="ndp" className="form-control" />
                                    </Col>

                                    <Col lg={3} md={6}>
                                        <label className="form-label fw-semibold">Number22</label>
                                        <Field name="number22" className="form-control" />
                                    </Col>
                                </Row>

                                <hr className="my-2" />

                                {/* VEHICLE DETAILS */}
                                <div className="fw-semibold text-dark mt-3 mb-2">Vehicle Details</div>
                                <Row className="g-3">
                                    <Col lg={4} md={6}>
                                        <label className="form-label fw-semibold">Model</label>
                                        <Field
                                            as="select"
                                            name="model_id"
                                            className="form-select bg-light"
                                            disabled
                                        >
                                            <option value="">Select Model</option>
                                            {modelslist.map(m => (
                                                <option key={m.id || m.value} value={m.id || m.value}>
                                                    {m.modalName || m.label}
                                                </option>
                                            ))}
                                        </Field>
                                    </Col>

                                    <Col lg={4} md={6}>
                                        <label className="form-label fw-semibold">Chassis</label>
                                        <Field
                                            as="select"
                                            name="chassis"
                                            className="form-select bg-light"
                                            disabled
                                        >
                                            <option value="">Select Chassis</option>
                                            {inventoryItem.chassisList.map(c => (
                                                <option key={c.value} value={c.value}>{c.label}</option>
                                            ))}
                                        </Field>
                                    </Col>

                                    <Col lg={4} md={12}>
                                        <label className="form-label fw-semibold">Engine</label>
                                        <Field as="select" name="engine" className="form-select bg-light" disabled>
                                            <option value="">Select Engine</option>
                                            {inventoryItem.engineList.map(e => (
                                                <option key={e.value} value={e.value}>{e.label}</option>
                                            ))}
                                        </Field>
                                    </Col>
                                </Row>
                            </Card.Body>

                            <Card.Footer className="bg-white border-0 px-4 pb-4 pt-0">
                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="outline-danger" onClick={() => navigate(-1)} style={{ minWidth: 120 }}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" className="px-4" style={{ minWidth: 140 }}>
                                        Save Changes
                                    </Button>
                                </div>
                            </Card.Footer>
                        </Card>

                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default EditDeliveryData;